import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel,
  MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { ItemChoice, ItemPicker, Loader, NeedsHousehold, PageHeader, SectionTitle, useConfirm, useLoad, useNotify } from '../common';
import { parseNumber, quantity, unitShort } from '../labels';
import { useGoBack } from '../editing';
import { NewLocationDialog, toInput } from './dialogs';
import { RhythmDialog, rhythmText } from './RhythmDialog';

const NO_LOCATION = '__none';
const NEW_LOCATION = '__new';

/** Empty → null; a number ≥ 0; undefined when it is not a valid number. */
function optional(text: string): number | null | undefined {
  if (!text.trim()) return null;
  const value = parseNumber(text);
  return value === null || value < 0 ? undefined : value;
}

interface ReserveRow { locationPublicId: T.Guid; locationName: string; quantity: string; }

/** "Guardado onde?": a place (not the main one, not already used) and how much is there. */
function AddReserveDialog({ open, options, locations, onClose, onAdd, onLocationCreated }: {
  open: boolean; options: T.StorageLocation[]; locations: T.StorageLocation[]; onClose: () => void;
  onAdd: (row: ReserveRow) => void; onLocationCreated: (location: T.StorageLocation) => void;
}) {
  const [location, setLocation] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => { if (open) { setLocation(''); setAmount(''); setError(null); } }, [open]);

  const add = () => {
    const chosen = options.find(l => l.publicId === location);
    if (!chosen) { setError('Escolhe onde está guardado.'); return; }
    const value = parseNumber(amount);
    if (value === null || value <= 0) { setError('Indica um número maior que zero.'); return; }
    onAdd({ locationPublicId: chosen.publicId, locationName: chosen.fullName, quantity: toInput(value) });
  };

  return (
    <>
      <Dialog open={open && !creating} onClose={onClose} maxWidth="xs" fullWidth>
        <form onSubmit={e => { e.preventDefault(); add(); }}>
          <DialogTitle>Guardado onde?</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <FormControl>
                <InputLabel id="reserve-location">Local</InputLabel>
                <Select
                  labelId="reserve-location"
                  label="Local"
                  value={location}
                  onChange={e => (e.target.value === NEW_LOCATION ? setCreating(true) : setLocation(e.target.value))}
                >
                  {options.map(l => <MenuItem key={l.publicId} value={l.publicId}>{l.fullName}</MenuItem>)}
                  <MenuItem value={NEW_LOCATION}><em>Outro sítio…</em></MenuItem>
                </Select>
              </FormControl>
              <TextField label="Quanto está lá?" value={amount} onChange={e => setAmount(e.target.value)} slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="contained">Juntar</Button>
          </DialogActions>
        </form>
      </Dialog>
      <NewLocationDialog
        open={open && creating}
        locations={locations}
        onClose={() => setCreating(false)}
        onCreated={l => { onLocationCreated(l); setLocation(l.publicId); setCreating(false); }}
      />
    </>
  );
}

/**
 * The item passed from the list, with its rules read again: they are set on the item's page, which may have just
 * changed them, and saving here must not put the old ones back. Opened by its address, the whole stock is read.
 */
async function loadStockItem(itemId: T.Guid, passed?: T.StockItem): Promise<T.StockItem | undefined> {
  if (passed?.catalogItemPublicId === itemId) {
    const rules = await api.getCatalogItem(itemId);
    return { ...passed, minStock: rules.minStock, reorderQuantity: rules.reorderQuantity, packSize: rules.packSize, dailyUse: rules.dailyUse };
  }
  return (await api.getStock()).find(i => i.catalogItemPublicId === itemId);
}

/** Stock of one item (/:itemId), or a new item to keep track of (/novo). */
const StockItemForm: React.FC = () => {
  const { itemId } = useParams();
  const goBack = useGoBack('/casa/em-casa');
  const navigate = useNavigate();
  const notify = useNotify();
  const [confirm, confirmDialog] = useConfirm();
  // Opened from "Em casa", the item comes with it (as on Android); only the first load uses it
  const { state } = useLocation();
  const passed = useRef((state as { item?: T.StockItem } | null)?.item);
  const { data, error: loadError, loading, reload } = useLoad(
    () => {
      const given = passed.current;
      passed.current = undefined;
      return Promise.all([itemId ? loadStockItem(itemId, given) : Promise.resolve(undefined), api.getStorageLocations()]);
    },
    [itemId],
  );
  const item = data?.[0];

  const [locations, setLocations] = useState<T.StorageLocation[]>([]);
  const [choice, setChoice] = useState<ItemChoice | null>(null);
  const [amount, setAmount] = useState('');
  // The buying rules are set on the item's page (catalog): only shown and kept here
  const [min, setMin] = useState<number | null>(null);
  const [location, setLocation] = useState(NO_LOCATION);
  const [reserves, setReserves] = useState<ReserveRow[]>([]);
  const [newLocation, setNewLocation] = useState(false);
  const [addingReserve, setAddingReserve] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (data) setLocations(data[1]); }, [data]);

  useEffect(() => {
    if (!item) return;
    setAmount(toInput(item.quantity));
    setMin(item.minStock ?? null);
    setLocation(item.locationPublicId ?? NO_LOCATION);
    setReserves(item.reserves.map(r => ({ locationPublicId: r.locationPublicId, locationName: r.locationName, quantity: toInput(r.quantity) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.catalogItemPublicId]);

  const unit = item ? item.unit : choice && 'item' in choice ? choice.item.defaultUnit : null;
  const mainId = location === NO_LOCATION ? null : location;
  const reserveOptions = locations.filter(l => l.publicId !== mainId && reserves.every(r => r.locationPublicId !== l.publicId));

  const addLocation = (created: T.StorageLocation) => {
    setLocations(list => list.concat(created));
    // Refetch so the order comes from the server
    api.getStorageLocations().then(setLocations).catch(() => undefined);
  };

  const picked = !item && choice && 'item' in choice ? choice.item : undefined;
  const currentMin = item ? min : picked?.minStock ?? null;
  const catalogId = item?.catalogItemPublicId ?? picked?.publicId;

  const save = async () => {
    setError(null);
    if (!item && !choice) { setError('Procura e escolhe o artigo.'); return; }
    const quantity = optional(amount);
    if (quantity === undefined) {
      setError('A quantidade deve ser um número (ou ficar em branco).');
      return;
    }
    const reserveValues = reserves.map(r => ({ locationPublicId: r.locationPublicId, quantity: parseNumber(r.quantity) }));
    if (reserveValues.some(r => r.quantity === null || r.quantity <= 0)) {
      setError('Indica quanto está guardado em cada sítio (um número maior que zero).');
      return;
    }
    // The count only changes when it was edited (the meals may have moved it meanwhile)
    const keep = !!item && amount === toInput(item.quantity);
    const body: T.SaveStockRequest = {
      catalogItemPublicId: item?.catalogItemPublicId ?? (choice && 'item' in choice ? choice.item.publicId : null),
      newItemName: !item && choice && 'newName' in choice ? choice.newName : null,
      quantity,
      keepQuantity: keep,
      minStock: currentMin,
      reorderQuantity: item ? item.reorderQuantity ?? null : picked?.reorderQuantity ?? null,
      locationPublicId: mainId,
      reserves: reserveValues.filter(r => r.locationPublicId !== mainId).map(r => ({ locationPublicId: r.locationPublicId, quantity: r.quantity as number })),
      // Kept as the item's page has them
      packSize: null,
      dailyUse: null,
    };
    setBusy(true);
    try {
      const saved = await api.saveStock(body);
      notify(`${saved.name} guardado.`, { severity: 'success' });
      goBack();
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!item) return;
    if (!await confirm({ title: 'Deixar de contar', text: `Deixar de contar «${item.name}»? (o artigo continua no catálogo)`, confirmText: 'Sim' })) return;
    setBusy(true);
    try {
      await api.removeStock(item.catalogItemPublicId);
      notify(`Deixaste de contar ${item.name}.`, { severity: 'success' });
      goBack();
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ mb: 1 }}>Em casa</Button>
      <PageHeader title={item ? item.name : 'Em casa'} subtitle={item ? undefined : 'Juntar um artigo a contar'} />
      <Loader loading={loading} error={loadError} onRetry={reload} hasData={data !== undefined}>
        {itemId && data && !item ? <Alert severity="warning">Este artigo já não está a ser contado.</Alert> : (
          <Stack spacing={2} component="form" onSubmit={e => { e.preventDefault(); save(); }}>
            {!item && (
              <Box>
                <ItemPicker value={choice} onChange={setChoice} label="Artigo" autoFocus />
                <Typography variant="caption" color="text.secondary">
                  Escolhe o artigo que já existe (o mesmo das receitas e da lista); só cria um novo se não estiver cá.
                </Typography>
              </Box>
            )}
            <TextField
              label={unit ? `Quanto tens em casa (${unitShort(unit)})` : 'Quanto tens em casa'}
              placeholder="Em branco = não contar"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'decimal' }, inputLabel: { shrink: true } }}
              helperText={item ? rhythmText(item) : undefined}
            />
            {item?.rhythmSuggestions && (
              <Box>
                <Button variant="outlined" size="small" onClick={() => setAdjusting(true)}>Ajustar ao ritmo</Button>
              </Box>
            )}
            <Box>
              <Typography variant="body2" color="text.secondary">
                {rulesText(currentMin, item ?? picked, unit)}
              </Typography>
              {catalogId && (
                <Button size="small" onClick={() => navigate(`/casa/catalogo/${catalogId}`)}>Mínimo, compra e gasto na ficha do artigo</Button>
              )}
            </Box>
            <FormControl>
              <InputLabel id="location-label">Onde fica (à mão)</InputLabel>
              <Select
                labelId="location-label"
                label="Onde fica (à mão)"
                value={location}
                onChange={e => (e.target.value === NEW_LOCATION ? setNewLocation(true) : setLocation(e.target.value))}
              >
                <MenuItem value={NO_LOCATION}>Sem local</MenuItem>
                {locations.map(l => (
                  <MenuItem key={l.publicId} value={l.publicId} sx={{ pl: l.parentPublicId ? 4 : 2 }}>{l.fullName}</MenuItem>
                ))}
                <MenuItem value={NEW_LOCATION}><em>Novo local…</em></MenuItem>
              </Select>
            </FormControl>

            <Box>
              <SectionTitle>Também guardado noutros sítios</SectionTitle>
              <Typography variant="body2" color="text.secondary">
                Por falta de espaço: o que não está à mão. As refeições gastam primeiro o que está à mão.
              </Typography>
              {reserves.map((r, index) => (
                <Stack key={r.locationPublicId} direction="row" spacing={1} sx={{ alignItems: 'center', mt: 1 }}>
                  <TextField
                    size="small"
                    label={r.locationName}
                    value={r.quantity}
                    onChange={e => setReserves(list => list.map((x, i) => (i === index ? { ...x, quantity: e.target.value } : x)))}
                    slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                    sx={{ flexGrow: 1 }}
                  />
                  <IconButton aria-label={`Tirar ${r.locationName}`} onClick={() => setReserves(list => list.filter((_, i) => i !== index))}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
              ))}
              <Button startIcon={<AddIcon />} onClick={() => setAddingReserve(true)} sx={{ mt: 0.5 }}>Guardado noutro sítio</Button>
            </Box>


            {error && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button type="submit" variant="contained" disabled={busy}>Guardar</Button>
              {item && <Button onClick={remove} disabled={busy}>Deixar de contar</Button>}
            </Stack>
          </Stack>
        )}
      </Loader>
      <NewLocationDialog
        open={newLocation}
        locations={locations}
        onClose={() => setNewLocation(false)}
        onCreated={l => { addLocation(l); setLocation(l.publicId); setNewLocation(false); }}
      />
      <AddReserveDialog
        open={addingReserve}
        options={reserveOptions}
        locations={locations}
        onClose={() => setAddingReserve(false)}
        onAdd={row => { setReserves(list => list.concat(row)); setAddingReserve(false); }}
        onLocationCreated={l => { addLocation(l); }}
      />
      {confirmDialog}
      <RhythmDialog
        item={adjusting && item ? item : null}
        onClose={updated => {
          setAdjusting(false);
          if (!updated) return;
          // Applied at once; the fields follow so "Guardar" keeps it
          setMin(updated.minStock ?? null);
          reload();
        }}
      />
    </Box>
  );
};

/** "Mínimo 2 · compra 6 · embalagem de 6 · 1 L por dia", set on the item's page. */
function rulesText(min: number | null, item: { reorderQuantity?: number | null; packSize?: number | null; dailyUse?: number | null } | undefined,
  unit: T.MeasureUnit | null): string {
  const parts: string[] = [];
  if (min !== null) parts.push(`mínimo ${quantity(min, unit)}`);
  if (item?.reorderQuantity) parts.push(`compra ${quantity(item.reorderQuantity, unit)}`);
  if (item?.packSize) parts.push(`embalagem de ${quantity(item.packSize, unit)}`);
  if (item?.dailyUse) parts.push(`${quantity(item.dailyUse, unit)} por dia`);
  return parts.length > 0 ? `Em casa: ${parts.join(' · ')}.` : 'Sem mínimo, compra ou gasto por dia definidos.';
}

const StockItemEditPage: React.FC = () => <NeedsHousehold><StockItemForm /></NeedsHousehold>;

export default StockItemEditPage;
